import { createInstance } from '@amplitude/analytics-browser';
import { computed, watch, onMounted } from 'vue';

const environment = computed(() => {
  if (typeof window !== 'undefined') {
    const { host } = window.location;

    if (host.includes("localhost")) return "local";

    if (host.includes("dev")) return "development";
  }

  return "production";
});

const apiKey = computed(() => {
  if (environment.value === "production") return import.meta.env.VITE_AMPLITUDE_API_KEY || "";

  if (environment.value === "development") return import.meta.env.VITE_AMPLITUDE_API_KEY_DEV || "";

  return "";
});

export default defineNuxtPlugin(async (nuxtApp) => {
  if (!apiKey.value) return;

  const amplitude = createInstance();
  nuxtApp.provide('amplitude', amplitude);

  const { cookiesEnabledIds, isConsentGiven } = useCookieControl();

  let isInitialized = false;

  const initAmplitude = (withTracking = false) => {
    if (!apiKey.value) return;

    amplitude.init(apiKey.value, undefined, {
      defaultTracking: false,
      trackingOptions: {
        ipAddress: !withTracking ? false : undefined,
        language: !withTracking ? false : undefined,
        platform: !withTracking ? false : undefined,
      },
      autocapture: {
        attribution: true,
        pageViews: true,
        sessions: true,
        formInteractions: true,
        fileDownloads: true,
        elementInteractions: true,
      },
    });

    isInitialized = true;
  };

  const reinitWithTracking = () => {
    initAmplitude(true);
  };

  watch(
    () => cookiesEnabledIds.value,
    (newVal, oldVal) => {
      const nowAccepted = newVal?.includes('performance_cookies') ?? false;
      const previouslyAccepted = oldVal?.includes('performance_cookies') ?? false;

      if (!previouslyAccepted && nowAccepted) {
        if (isInitialized) {
          reinitWithTracking();
        } else {
          initAmplitude(true);
        }
      }
    },
    { immediate: true }
  );

  const consented = isConsentGiven.value && cookiesEnabledIds.value?.includes('performance_cookies');
  initAmplitude(consented);
});

declare module '#app' {
  interface NuxtApp {
    $amplitude: ReturnType<typeof createInstance>;
  }
}